'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { ArrowLeft, Send, MapPin, Phone, Mail, Clock, CheckCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function KontaktPage() {
  const t = useTranslations('contactPage');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    businessType: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const businessTypes = [
    { value: 'hairdresser', label: t('form.businessTypes.hairdresser') },
    { value: 'beauty', label: t('form.businessTypes.beauty') },
    { value: 'physio', label: t('form.businessTypes.physio') },
    { value: 'dentist', label: t('form.businessTypes.dentist') },
    { value: 'massage', label: t('form.businessTypes.massage') },
    { value: 'vacation', label: t('form.businessTypes.vacation') },
    { value: 'hotel', label: t('form.businessTypes.hotel') },
    { value: 'restaurant', label: t('form.businessTypes.restaurant') },
    { value: 'fitness', label: t('form.businessTypes.fitness') },
    { value: 'other', label: t('form.businessTypes.other') },
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error sending message');
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert(
        error instanceof Error
          ? error.message
          : 'There was a problem sending your message. Please try again later.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-4 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <Card className="border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06),0_24px_56px_-16px_rgba(15,23,42,0.10)]">
            <CardContent className="p-12 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-blue-50"
              >
                <CheckCircle className="h-10 w-10 text-green-600" />
              </motion.div>

              <h2 className="mb-4 text-2xl font-bold text-gray-900">{t('success.title')}</h2>

              <p className="mb-6 text-gray-600">{t('success.message')}</p>

              <Button asChild className="bg-gray-950 hover:bg-gray-800">
                <Link href="/">{t('success.backButton')}</Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/80 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link
              href="/"
              className="flex items-center text-blue-600 transition-colors hover:text-blue-700"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              {t('header.backButton')}
            </Link>
            <Badge variant="secondary" className="bg-blue-50 text-blue-700">
              {t('header.badge')}
            </Badge>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="items-start lg:grid lg:grid-cols-2 lg:gap-16">
          {/* Left Side - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 lg:mb-0"
          >
            <div className="text-center lg:text-left">
              <h1 className="mb-6 text-3xl font-bold text-gray-900 sm:text-4xl lg:text-5xl">
                {t('hero.title')}
                <span className="block bg-blue-600 bg-clip-text text-transparent">
                  {t('hero.titleHighlight')}
                </span>
              </h1>
              <p className="mb-8 max-w-2xl text-xl text-gray-600">{t('hero.subtitle')}</p>
            </div>

            {/* Contact Information Cards */}
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <Card className="border-0 shadow-lg transition-shadow hover:shadow-xl">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600">
                        <Mail className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="mb-1 font-semibold text-gray-900">
                          {t('contactInfo.email.title')}
                        </h3>
                        <p className="text-gray-600">{t('contactInfo.email.value')}</p>
                        <p className="mt-1 text-sm text-gray-500">{t('contactInfo.email.note')}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Card className="border-0 shadow-lg transition-shadow hover:shadow-xl">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600">
                        <Phone className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="mb-1 font-semibold text-gray-900">
                          {t('contactInfo.phone.title')}
                        </h3>
                        <p className="text-gray-600">{t('contactInfo.phone.value')}</p>
                        <p className="mt-1 text-sm text-gray-500">{t('contactInfo.phone.note')}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Card className="border-0 shadow-lg transition-shadow hover:shadow-xl">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600">
                        <MapPin className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="mb-1 font-semibold text-gray-900">
                          {t('contactInfo.address.title')}
                        </h3>
                        <p className="text-gray-600">
                          {t('contactInfo.address.company')}
                          <br />
                          {t('contactInfo.address.street')}
                          <br />
                          {t('contactInfo.address.city')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Card className="border-0 shadow-lg transition-shadow hover:shadow-xl">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600">
                        <Clock className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="mb-1 font-semibold text-gray-900">
                          {t('contactInfo.hours.title')}
                        </h3>
                        <div className="space-y-1 text-gray-600">
                          <p>{t('contactInfo.hours.weekdays')}</p>
                          <p>{t('contactInfo.hours.saturday')}</p>
                          <p>{t('contactInfo.hours.sunday')}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Side - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06),0_24px_56px_-16px_rgba(15,23,42,0.10)]">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl font-bold text-gray-900">
                  {t('form.title')}
                </CardTitle>
                <p className="text-gray-600">{t('form.subtitle')}</p>
              </CardHeader>

              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Fields */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="firstName">
                        {t('form.fields.firstName')} {t('form.fields.required')}
                      </Label>
                      <Input
                        id="firstName"
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">
                        {t('form.fields.lastName')} {t('form.fields.required')}
                      </Label>
                      <Input
                        id="lastName"
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="email">
                        {t('form.fields.email')} {t('form.fields.required')}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">{t('form.fields.phone')}</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Business Info */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="company">{t('form.fields.company')}</Label>
                      <Input
                        id="company"
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className="mt-1"
                        placeholder={t('form.fields.companyPlaceholder')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="businessType">{t('form.fields.businessType')}</Label>
                      <Select
                        value={formData.businessType}
                        onValueChange={(value) => handleInputChange('businessType', value)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue
                            placeholder={t('form.fields.businessTypePlaceholder')}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {businessTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <Label htmlFor="subject">
                      {t('form.fields.subject')} {t('form.fields.required')}
                    </Label>
                    <Input
                      id="subject"
                      type="text"
                      value={formData.subject}
                      onChange={(e) => handleInputChange('subject', e.target.value)}
                      className="mt-1"
                      placeholder={t('form.fields.subjectPlaceholder')}
                      required
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message">
                      {t('form.fields.message')} {t('form.fields.required')}
                    </Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="mt-1 min-h-[120px]"
                      placeholder={t('form.fields.messagePlaceholder')}
                      required
                    />
                  </div>

                  {/* Privacy Notice */}
                  <Alert>
                    <AlertDescription className="text-sm">
                      {t('form.privacy.text')}{' '}
                      <Link
                        href="/datenschutz"
                        className="text-blue-600 underline hover:text-blue-700"
                      >
                        {t('form.privacy.link')}
                      </Link>{' '}
                      {t('form.privacy.textEnd')}
                    </AlertDescription>
                  </Alert>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="h-12 w-full bg-blue-600 font-semibold text-white hover:bg-blue-700"
                  >
                    {isSubmitting ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="mr-2 h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                        />
                        {t('form.submitting')}
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        {t('form.submit')}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
